/**
 * IAM User Service
 * Handles CRUD operations for users via the IAM API
 */

import axios from 'axios';

// Get auth token from localStorage
const getAuthToken = () => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create IAM API instance
const iamApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/iam/api' : '/iam/api',
});

// Add request interceptor to include auth token
iamApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
iamApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Types
export interface User {
    id: string;
    username: string;
    email?: string;
    emailVerified?: boolean;
    firstName?: string;
    lastName?: string;
    enabled?: boolean;
    createdTimestamp?: number;
    attributes?: Record<string, string[]>;
    realmRoles?: string[];
    groups?: string[];
}

export interface UserListResponse {
    content: User[];
    count: number;
    page: number;
    size: number;
}

export interface CreateUserRequest {
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    password: string;
    orgId: string;
    enabled?: boolean;
}

export interface UpdateUserRequest {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    enabled?: boolean;
}

export interface ResetPasswordRequest {
    id: string;
    password: string;
}

// User Service
export class UserService {
    private basePath = '/users';

    /**
     * Get paginated list of users for an organization
     */
    async getUsers(
        orgId: string,
        search: string = '',
        page: number = 0,
        size: number = 100
    ): Promise<UserListResponse> {
        const response = await iamApi.get<UserListResponse>(this.basePath, {
            params: { orgId, search, page, size },
        });
        return response.data;
    }

    /**
     * Get a single user by ID
     */
    async getUser(userId: string): Promise<User> {
        const response = await iamApi.get<User>(`${this.basePath}/${userId}`);
        return response.data;
    }

    /**
     * Create a new user
     */
    async createUser(data: CreateUserRequest): Promise<User> {
        const response = await iamApi.post<User>(`${this.basePath}/create`, data);
        return response.data;
    }

    /**
     * Update an existing user
     */
    async updateUser(data: UpdateUserRequest): Promise<User> {
        const response = await iamApi.post<User>(`${this.basePath}/update`, data);
        return response.data;
    }

    /**
     * Reset user password
     */
    async resetPassword(userId: string, password: string): Promise<User> {
        const response = await iamApi.post<User>(`${this.basePath}/reset-password`, {
            id: userId,
            password,
        });
        return response.data;
    }

    /**
     * Delete a user
     */
    async deleteUser(userId: string): Promise<void> {
        await iamApi.delete(`${this.basePath}/${userId}`);
    }
}

// Singleton instance
let userServiceInstance: UserService | null = null;

export function getUserService(): UserService {
    if (!userServiceInstance) {
        userServiceInstance = new UserService();
    }
    return userServiceInstance;
}

export default UserService;
